import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

import dataApi from '@/shared/services/dataApi';
import isSafeUrl from '@/shared/utils/urlHelpers';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import SEO from '@/components/common/SEO';

import DataProductCard from './components/DataProductCard';
import DataSourceCard from './components/DataSourceCard';

export default function OrganizationDetail() {
    const { id } = useParams();
    const { openChat } = useChatPageState();
    const [org, setOrg] = useState(null);
    const [sources, setSources] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Publish org detail context so Bili knows what the user is viewing
    useChatPageEffect(
        {
            page: 'organization-detail',
            section: 'data-discovery',
            organizationName: org?.org_name,
            organizationType: org?.org_type,
            sourceCount: sources.length,
            productCount: products.length,
        },
        [org?.org_name, sources.length, products.length]
    );

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const orgData = await dataApi.getOrganization(id);
                if (cancelled) return;
                // A resolved-but-empty response means the org was not found.
                // Render the not-found alert via the `!org` gate rather than
                // dereferencing a null org_name below.
                if (!orgData) {
                    setLoading(false);
                    return;
                }
                setOrg(orgData);

                // Fetch related sources and products by org name
                const [srcRes, prodRes] = await Promise.all([
                    dataApi.getSources({
                        organization: orgData.org_name,
                        limit: 50,
                    }),
                    dataApi.getProducts({
                        organization: orgData.org_name,
                        limit: 50,
                    }),
                ]);
                if (!cancelled) {
                    setSources(srcRes.data_sources || []);
                    setProducts(prodRes.data_products || []);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5 text-center">
                    <Spinner animation="border" variant="primary" />
                    <span className="visually-hidden">Loading...</span>
                </Container>
            </section>
        );
    }

    if (error || !org) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5">
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error || 'Organization not found.'}</p>
                        <Link to="/data" className="btn btn-outline-primary">
                            Back to Discovery
                        </Link>
                    </Alert>
                </Container>
            </section>
        );
    }

    const location = [org.location_city, org.location_state]
        .filter(Boolean)
        .join(', ');

    return (
        <section className="page-container bg-light">
            <SEO title={org?.org_name || 'Organization'} />
            <Container className="py-4">
                {/* Breadcrumb */}
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <Link to="/data">Data Discovery</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/data?tab=organizations">
                                Organizations
                            </Link>
                        </li>
                        <li
                            className="breadcrumb-item active"
                            aria-current="page"
                        >
                            {org.org_name}
                        </li>
                    </ol>
                </nav>

                <Row className="g-4">
                    {/* Main content */}
                    <Col lg={8}>
                        <h1 className="h3 text-primary fw-bold mb-3">
                            {org.org_name}
                        </h1>

                        {org.description && (
                            <p className="text-body-secondary mb-4">
                                {org.description}
                            </p>
                        )}

                        {/* Data Sources */}
                        {sources.length > 0 && (
                            <section className="mb-4">
                                <h2 className="h5 text-primary mb-3">
                                    Data Sources
                                    <Badge
                                        bg="light"
                                        text="dark"
                                        className="ms-2 fw-normal"
                                    >
                                        {sources.length}
                                    </Badge>
                                </h2>
                                <Row className="g-3">
                                    {sources.map((s) => (
                                        <Col key={s.id} md={6}>
                                            <DataSourceCard source={s} />
                                        </Col>
                                    ))}
                                </Row>
                            </section>
                        )}

                        {/* Data Products */}
                        {products.length > 0 && (
                            <section className="mb-4">
                                <h2 className="h5 text-primary mb-3">
                                    Data Products
                                    <Badge
                                        bg="light"
                                        text="dark"
                                        className="ms-2 fw-normal"
                                    >
                                        {products.length}
                                    </Badge>
                                </h2>
                                <Row className="g-3">
                                    {products.map((p) => (
                                        <Col key={p.id} md={6}>
                                            <DataProductCard product={p} />
                                        </Col>
                                    ))}
                                </Row>
                            </section>
                        )}

                        {sources.length === 0 && products.length === 0 && (
                            <p className="text-muted">
                                No data sources or products linked to this
                                organization yet.
                            </p>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body>
                                <Card.Title className="h6 text-primary mb-3">
                                    Details
                                </Card.Title>
                                <dl className="mb-0">
                                    {org.org_type && (
                                        <>
                                            <dt className="small text-muted">
                                                Type
                                            </dt>
                                            <dd>
                                                <Badge bg="secondary">
                                                    {org.org_type}
                                                </Badge>
                                            </dd>
                                        </>
                                    )}
                                    {location && (
                                        <>
                                            <dt className="small text-muted">
                                                Location
                                            </dt>
                                            <dd>{location}</dd>
                                        </>
                                    )}
                                    <dt className="small text-muted">Status</dt>
                                    <dd>
                                        <Badge
                                            bg={
                                                org.status === 'active'
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                        >
                                            {org.status}
                                        </Badge>
                                    </dd>
                                </dl>
                            </Card.Body>
                        </Card>

                        {/* Links */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body className="d-flex flex-column gap-2">
                                {org.website && isSafeUrl(org.website) && (
                                    <a
                                        href={org.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary rounded-pill"
                                    >
                                        Visit Website
                                    </a>
                                )}
                                <Button
                                    variant="outline-primary"
                                    className="rounded-pill"
                                    onClick={() =>
                                        openChat(
                                            `Tell me about data from ${org.org_name}`
                                        )
                                    }
                                >
                                    Ask Bili About This
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
